import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Linkedin, Mail, Newspaper, ExternalLink } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, getDocs, query } from 'firebase/firestore';

const MediaPage = ({ type }) => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [type]);

    const isSocial = type === 'sosyal';
    const title = isSocial ? "Sosyal Medya Hesaplarımız" : "Basında Biz";
    const subtitle = isSocial
        ? "Bizi takip edin, etkinliklerimizden ve duyurularımızdan anında haberdar olun."
        : "Ulusal ve yerel basında MSKÜ TURET hakkında çıkan haberler.";

    // Social Links (Static)
    const socialLinks = [
        {
            name: "Instagram",
            url: "https://www.instagram.com/mskuturet?igsh=MTN5aG9taXRzYjEwZA==",
            icon: <Instagram className="w-8 h-8" />,
            color: "bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500"
        },
        {
            name: "LinkedIn",
            url: "https://www.linkedin.com/in/msk%C3%BC-turist-rehberli%C4%9Fi-toplulu%C4%9Fu-809140357/?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
            icon: <Linkedin className="w-8 h-8" />,
            color: "bg-[#0077b5]"
        },
        {
            name: "E-posta",
            url: "mailto:mskuturistrehberligihesabi@gmail.com",
            icon: <Mail className="w-8 h-8" />,
            color: "bg-emerald-500"
        }
    ];

    // Data Fetching for Press
    const [pressItems, setPressItems] = useState([]);
    const [loading, setLoading] = useState(!isSocial);

    useEffect(() => {
        if (!isSocial) {
            const fetchPress = async () => {
                try {
                    const q = query(collection(db, "press"));
                    const querySnapshot = await getDocs(q);
                    const data = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    // Sort by Date Descending
                    data.sort((a, b) => {
                        if (!a.date || !b.date) return 0;
                        if (a.date === 'Güncel') return -1;
                        if (b.date === 'Güncel') return 1;
                        const [d1, m1, y1] = a.date.split('.');
                        const [d2, m2, y2] = b.date.split('.');
                        return new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1);
                    });

                    setPressItems(data);
                } catch (error) {
                    console.error("Error fetching press items:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchPress();
        }
    }, [isSocial]);

    return (
        <div className="bg-modern-blue-50 text-modern-blue-900 min-h-screen">
            <main className="pt-32 pb-20 px-6">
                <div className="container mx-auto max-w-6xl">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-modern-blue-900 via-modern-blue-600 to-emerald-500 mb-6 drop-shadow-sm font-heading">
                            {title}
                        </h1>
                        <p className="text-modern-blue-600 max-w-2xl mx-auto text-lg leading-relaxed">
                            {subtitle}
                        </p>
                    </motion.div>

                    {isSocial ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
                            {socialLinks.map((link, idx) => (
                                <motion.a
                                    key={idx}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                    className="block p-8 rounded-3xl bg-white border border-modern-blue-100 shadow-xl hover:shadow-2xl transition-all group relative overflow-hidden"
                                >
                                    <div className={`absolute top-0 right-0 w-32 h-32 ${link.color} opacity-10 rounded-bl-full transition-transform group-hover:scale-110`} />

                                    <div className={`w-16 h-16 rounded-2xl ${link.color} text-white flex items-center justify-center mb-6 shadow-lg group-hover:rotate-6 transition-transform`}>
                                        {link.icon}
                                    </div>

                                    <h3 className="text-2xl font-bold text-modern-blue-900 mb-2">{link.name}</h3>
                                    <div className="flex items-center gap-2 text-modern-blue-500 font-medium">
                                        <span>{link.name === 'E-posta' ? 'Mail Gönder' : 'Ziyaret Et'}</span>
                                        <ExternalLink className="w-4 h-4" />
                                    </div>
                                </motion.a>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {pressItems.map((item, idx) => (
                                <motion.a
                                    key={idx}
                                    href={item.url}
                                    target={item.url !== "#" ? "_blank" : "_self"}
                                    rel="noopener noreferrer"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: idx * 0.1 }}
                                    whileHover={{ y: -5 }}
                                    className="bg-white rounded-3xl p-8 border border-modern-blue-100 shadow-lg hover:shadow-cyan-500/20 hover:border-cyan-400 transition-all flex flex-col block cursor-pointer group h-full"
                                >
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="bg-modern-blue-50 px-4 py-1.5 rounded-full text-sm font-bold text-modern-blue-800 border border-modern-blue-100 group-hover:bg-cyan-50 group-hover:text-cyan-700 transition-colors">
                                            {item.source}
                                        </div>
                                        <div className="flex items-center gap-2 text-gray-400 text-xs font-medium">
                                            <Newspaper className="w-4 h-4" />
                                            {item.date}
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-bold text-modern-blue-900 mb-4 leading-tight group-hover:text-cyan-600 transition-colors">
                                        {item.title}
                                    </h3>

                                    <p className="text-modern-blue-600/80 text-sm mb-6 flex-1 leading-relaxed">
                                        {item.summary}
                                    </p>

                                    <div className="flex items-center text-cyan-500 font-bold text-sm group-hover:translate-x-2 transition-transform">
                                        <span>Haberi Kaynağında Oku</span>
                                        <ExternalLink className="w-4 h-4 ml-2" />
                                    </div>
                                </motion.a>
                            ))}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default MediaPage;
